import React, { useState } from 'react';
import { ChevronRight, Info } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import RequestForm from './services/RequestForm';
import { useLanguage } from '../contexts/LanguageContext';

const months = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
const monthsEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthsZh = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];

interface Category {
  name: string;
  baseRates: {
    fast: number;
    normal: number;
    slow: number;
  };
}

const categories: Category[] = [
  { 
    name: 'Одежда', 
    baseRates: { fast: 3.0, normal: 2.6, slow: 2.2 } 
  },
  { 
    name: 'Clothing', 
    baseRates: { fast: 3.0, normal: 2.6, slow: 2.2 } 
  },
  { 
    name: '服装', 
    baseRates: { fast: 3.0, normal: 2.6, slow: 2.2 } 
  },
  { 
    name: 'Хоз. товары', 
    baseRates: { fast: 2.4, normal: 1.5, slow: 1.2 } 
  },
  { 
    name: 'Хоз. товары',
    baseRates: { fast: 2.4, normal: 1.5, slow: 1.2 }
  },
  {
    name: 'Игрушки',
    baseRates: { fast: 1.7, normal: 1.5, slow: 1.2 }
  },
  {
    name: 'Нижнее белье',
    baseRates: { fast: 2.0, normal: 1.7, slow: 0.9 }
  },
  {
    name: 'Инструменты',
    baseRates: { fast: 1.3, normal: 1.2, slow: 1.1 }
  }
];

function PricingPage() {
  const { t, language } = useLanguage();
  const [showForm, setShowForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return categories.find(cat => language === 'ru' ? cat.name === 'Одежда' : language === 'en' ? cat.name === 'Clothing' : cat.name === '服装') || categories[0];
  });

  // Get the appropriate months array based on language
  const generateMonthlyData = (baseRates: Category['baseRates']) => {
    return months.map(month => ({
      month,
      fast: baseRates.fast + (Math.random() * 0.2 - 0.1),
      normal: baseRates.normal + (Math.random() * 0.2 - 0.1),
      slow: baseRates.slow + (Math.random() * 0.2 - 0.1)
    }));
  };

  const generateMonthlyDataByLanguage = (baseRates: Category['baseRates']) => {
    const monthsArray = language === 'ru' ? months : language === 'en' ? monthsEn : monthsZh;
    
    return monthsArray.map(month => ({
      month,
      fast: baseRates.fast + (Math.random() * 0.2 - 0.1),
      normal: baseRates.normal + (Math.random() * 0.2 - 0.1),
      slow: baseRates.slow + (Math.random() * 0.2 - 0.1)
    }));
  };

  // Filter categories based on language
  const filteredCategories = categories.filter(cat => {
    return language === 'ru' ? !cat.name.includes('Clothing') && !cat.name.includes('服装') : language === 'en' ? cat.name.includes('Clothing') : cat.name.includes('服装');
  });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
          <p className="text-sm text-[#FF3B30]">
            {language === 'ru' ? 'Быстрое авто' : language === 'en' ? 'Fast Auto' : '快速汽车'}: ${payload[0].value.toFixed(2)}
          </p>
          <p className="text-sm text-[#34C759]">
            {language === 'ru' ? 'Обычное авто' : language === 'en' ? 'Regular Auto' : '普通汽车'}: ${payload[1].value.toFixed(2)}
          </p>
          <p className="text-sm text-[#007AFF]">
            {language === 'ru' ? 'Медленное авто' : language === 'en' ? 'Slow Auto' : '慢速汽车'}: ${payload[2].value.toFixed(2)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B3BA0]/50 via-[#1B3BA0]/30 to-transparent mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {language === 'ru' 
                ? 'Почему нельзя указать точную цену доставки?'
                : language === 'en' 
                ? 'Why can\'t we specify the exact delivery price?'
                : '为什么我们无法指定确切的交付价格？'}
            </h1>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              {language === 'ru' 
                ? 'Стоимость доставки из Китая варьируется от месяца к месяцу и зависит от объёма, типа товара, сезона и выбранного способа доставки.'
                : language === 'en' 
                ? 'The cost of delivery from China varies from month to month and depends on the volume, type of goods, season and chosen delivery method.'
                : '从中国的交付成本每月都有变化，取决于货物的体积、类型、季节和所选的交付方式。'}
            </p>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              {language === 'ru' 
                ? 'Чтобы вы понимали ориентиры и динамику цен — мы подготовили наглядный график. А для получения точной стоимости под ваш заказ — просто оставьте заявку, и наш менеджер пришлёт вам коммерческое предложение в течение 5 минут.'
                : language === 'en' 
                ? 'To help you understand price benchmarks and dynamics, we have prepared a visual chart. For an exact cost for your order, simply leave a request, and our manager will send you a commercial offer within 5 minutes.'
                : '为了帮助您了解价格基准和动态，我们准备了一个可视化图表。要获取您订单的确切成本，只需留下请求，我们的经理将在5分钟内向您发送商业报价。'}
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center bg-white text-[#1B3BA0] px-8 py-4 rounded-full hover:bg-[#99BBFF] transition-all font-medium text-lg group shadow-lg hover:shadow-xl"
            >
              {language === 'ru' ? 'Получить расчёт' : 
               language === 'en' ? 'Get a Quote' : 
               '获取报价'}
              <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Search and Categories */}
      <section className="py-12 px-6 bg-[#f5f5f7]">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-3xl font-bold text-[#1B3BA0] mb-8 text-center">
            {language === 'ru' ? 'Динамика цен на доставку в 2024 году' : 
             language === 'en' ? 'Delivery Price Dynamics in 2024' : 
             '2024年交付价格动态'}
          </h2>
          
          {/* Category Selection */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {filteredCategories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  selectedCategory.name === cat.name
                    ? 'bg-[#1B3BA0] text-white'
                    : 'bg-white text-[#1d1d1f] hover:bg-gray-50'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Price Info */}
          <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-[#1B3BA0]" />
              <p className="text-gray-600">
                {language === 'ru' 
                  ? 'График показывает колебания цен на доставку в течение года с учетом сезонности'
                  : language === 'en' 
                  ? 'The chart shows fluctuations in delivery prices throughout the year, taking into account seasonality'
                  : '图表显示全年交付价格的波动，考虑到季节性因素'}
              </p>
            </div>
            
            {/* Chart */}
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={generateMonthlyDataByLanguage(selectedCategory.baseRates)}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" />
                  <YAxis
                    domain={['dataMin - 0.1', 'dataMax + 0.1']}
                    tickFormatter={(value) => `$${value.toFixed(1)}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="fast"
                    name={language === 'ru' ? 'Быстрое авто (10-15 дней)' : language === 'en' ? 'Fast Auto (10-15 days)' : '快速汽车 (10-15天)'}
                    stroke="#FF3B30"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 8 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="normal"
                    name={language === 'ru' ? 'Обычное авто (15-20 дней)' : language === 'en' ? 'Regular Auto (15-20 days)' : '普通汽车 (15-20天)'}
                    stroke="#34C759"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 8 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="slow"
                    name={language === 'ru' ? 'Медленное авто (18-25 дней)' : language === 'en' ? 'Slow Auto (18-25 days)' : '慢速汽车 (18-25天)'}
                    stroke="#007AFF"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div className="mt-6 text-sm text-gray-500 text-center">
              {language === 'ru' 
                ? '* Цены указаны за 1 кг груза и могут варьироваться в зависимости от объема и сезона'
                : language === 'en' 
                ? '* Prices are per 1 kg of cargo and may vary depending on volume and season'
                : '* 价格按每公斤货物计算，可能根据体积和季节而变化'}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {language === 'ru' ? 'Получить расчёт стоимости' : 
             language === 'en' ? 'Get a Cost Estimate' : 
             '获取费用估算'}
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            {language === 'ru' 
              ? 'Заполните форму, и мы свяжемся с вами для обсуждения деталей'
              : language === 'en' 
              ? 'Fill out the form, and we will contact you to discuss the details'
              : '填写表格，我们将联系您讨论详情'}
          </p>
          <RequestForm source={language === 'ru' ? "Цены" : language === 'en' ? "Prices" : "价格"} />
        </div>
      </section>

      {/* Request Form Popup */}
      {showForm && <RequestForm isPopup onClose={() => setShowForm(false)} source={language === 'ru' ? "Цены" : language === 'en' ? "Prices" : "价格"} />}
    </div>
  );
}

export default PricingPage;